import {
  Button,
  ButtonsGroup,
  Dropzone,
  Link,
  Modal,
  ModalProps,
  notify,
  Space,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useCsvExport } from '@features/exports/hooks/useCsvExport'
import { useFetchProducts } from '@features/products/hooks/useFetchProducts'
import { useSelectableProductAccounts } from '@features/products/hooks/useSelectableProductAccounts'
import { getProductsExportData } from '@features/products/utils/getProductsExportData'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../config/queryClient'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { APIError } from '../../../../utils'
import { ProductsImportResult } from './elements/ProductsImportResult'
import { useImportProducts } from './hooks/useImportProducts'
import { useTrackProductsImport } from './hooks/useTrackProductsImport'
import { ImportProductsResult } from './types'

const acceptCSV = { 'text/csv': ['.csv'], 'text/plain': ['.csv'] }

const ProductsImportModalComponent = (modalProps: ModalProps): ReactElement => {
  const [importResult, setImportResult] = useState<ImportProductsResult>()
  const { t } = useTranslation()
  const { close } = useModal(modalProps.id)
  const { organization } = useUserOrganization()
  const { trackImport } = useTrackProductsImport()

  const isFrenchOrganization = organization?.countryId === 'FR'

  const handleImportSuccess = useCallback(
    (result: ImportProductsResult) => {
      const { created, invalidProductTypeRows, submitted } = result

      if (created === 0) {
        trackImport({ phase: 'Failed', errorReason: 'none_imported' })
      } else {
        trackImport({ phase: 'Completed', itemCount: created })
        queryClient.invalidateQueries(QueryKeys.Products)
      }

      if (invalidProductTypeRows.length > 0) {
        setImportResult(result)
        return
      }

      close()

      if (created === 0) {
        notify({
          id: NotificationKeys.ProductsImport,
          variant: 'error',
          message: t('products.import.none_imported'),
        })
        return
      }

      if (created < submitted) {
        notify({
          id: NotificationKeys.ProductsImport,
          variant: 'warning',
          message: (
            <Trans
              i18nKey="products.import.partial_notification"
              values={{ created, submitted }}
              components={{ 1: <strong /> }}
            />
          ),
        })
        return
      }

      notify({
        id: NotificationKeys.ProductsImport,
        variant: 'success',
        message: (
          <Trans
            i18nKey="products.import.success_notification"
            values={{ count: created }}
            components={{ 1: <strong /> }}
          />
        ),
      })
    },
    [close, t, trackImport],
  )

  const handleImportError = useCallback(
    (error: APIError) => {
      trackImport({ phase: 'Failed', errorReason: error.message || 'unknown' })
      close()
      notify({
        id: NotificationKeys.ProductsImport,
        variant: 'error',
        message: t('products.import.error'),
      })
    },
    [close, t, trackImport],
  )

  const { importProducts, isImporting, isLoadingImportData, reset } = useImportProducts({
    onSuccess: handleImportSuccess,
    onError: handleImportError,
  })

  const { products } = useFetchProducts()
  const { accounts } = useSelectableProductAccounts({ enabled: isFrenchOrganization })
  const exampleSpreadSheetData = useMemo(
    () => getProductsExportData(products, t, isFrenchOrganization ? { accounts } : {}),
    [accounts, isFrenchOrganization, products, t],
  )
  const { exportAsCsv: downloadExample } = useCsvExport({
    spreadSheetData: exampleSpreadSheetData,
    fileName: t('products.import.example_file_name'),
    notificationKey: NotificationKeys.ProductsExport,
  })

  const handleDropAccepted = useCallback(
    ([file]: File[]) => {
      trackImport({ phase: 'Started' })
      reset()
      importProducts(file)
    },
    [importProducts, reset, trackImport],
  )

  return (
    <Modal id={modalProps.id}>
      <Modal.Header title={importResult ? t('products.import.result_title') : t('products.import.modal_title')} />
      <Modal.Body>
        {importResult ? (
          <ProductsImportResult result={importResult} />
        ) : (
          <>
            <Text colorVariant="secondary">
              <Trans
                i18nKey="products.import.description"
                components={{
                  1: <Link onClick={downloadExample} withAnimation />,
                  2: <strong />,
                }}
              />
            </Text>
            <Text colorVariant="secondary">
              <Trans i18nKey="products.import.description_hint" />
            </Text>

            <Space size="s16" />

            <Dropzone
              accept={acceptCSV}
              contentText={t('products.import.dropzone_content')}
              disabled={isLoadingImportData}
              footerText={t('products.import.dropzone_footer')}
              isUploading={isImporting}
              maxFiles={1}
              onDropAccepted={handleDropAccepted}
              uploadingText={t('products.import.uploading')}
            />
          </>
        )}
      </Modal.Body>
      {importResult && (
        <Modal.Footer>
          <ButtonsGroup>
            <Button onClick={close}>{t('close')}</Button>
          </ButtonsGroup>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export const ProductsImportModal = withModalConditionalRender(ProductsImportModalComponent)
